import type {
  IFieldSet,
  IFieldSetOverview,
  TFieldSetItem,
  IFieldSetWithFieldsConvertedToEntityMaskItems,
  TFieldSetFieldValidations,
  TFieldSetFieldActionVerb,
} from '../types/fieldSet/models'
import { setItemsForSelect } from './entityInputFieldsService/getSelectList'
import { selectFromOptionIds } from './selectLists/fieldSetFieldSelectFromOptions'
import { getLocationsForCompanyId } from './companies'
import { httpGet, httpPost, httpPatchMerge, httpDelete } from './http'
import { getSuppliers } from '~/services/suppliers'
import { isSpacer, isDivider, isEntityMaskData } from '~/types/models'
import type {
  TEntityId,
  TEntityMaskItem,
  IEntityMaskData,
  IEntityFieldValidation,
  IEntityDataItem,
  TEntityVersion,
  ISelectItem,
  IEntityParentObjectForCreate,
} from '~/types/models'

const mn = '[services/fieldSets.ts]'
const apiEntityPath = '/api/fieldsets'

export async function getFieldSets({ apiPath = apiEntityPath } = {}) {
  const callingMn = getFieldSets.name
  return httpGet<IFieldSet[]>({ callingMn, apiPath })
}

export async function getFieldSetOverview(id: string) {
  const callingMn = getFieldSetOverview.name
  const apiPath = `${apiEntityPath}/${id}/overview`
  return httpGet<IFieldSetOverview>({ callingMn, apiPath })
}

export async function getFieldSetFieldsForFieldSet(id: string) {
  const fieldSetOverview = await getFieldSetOverview(id)
  return fieldSetOverview.fields
}

export async function getFieldSetWithFieldsConvertedToEntityMaskItems({
  id,
  companyId,
}: {
  id: TEntityId
  companyId?: TEntityId
}): Promise<IFieldSetWithFieldsConvertedToEntityMaskItems> {
  // This should probably use a specific call for fieldSetWithFields
  // even though fieldSetOverview contains all the data we need. JR.
  const fieldSetOverview = await getFieldSetOverview(id)
  const convertedFields = fieldSetOverview.fields
    ? convertFieldSetFieldsToEntityMaskItems(fieldSetOverview.fields, companyId)
    : []
  const fieldSetWithFieldsConvertedToEntityMaskItems: IFieldSetWithFieldsConvertedToEntityMaskItems =
    {
      fieldSet: fieldSetOverview.fieldSet,
      fields: convertedFields,
    }
  return fieldSetWithFieldsConvertedToEntityMaskItems
}

export async function createFieldSet(entity: IFieldSet) {
  const callingMn = createFieldSet.name
  const apiPath = apiEntityPath
  return httpPost<IFieldSet>({ callingMn, apiPath, entity })
}

export async function updateFieldSet(
  entityData: Partial<IFieldSet>,
  id: TEntityId
) {
  const callingMn = updateFieldSet.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialIFieldSet = Partial<IFieldSet>

  return httpPatchMerge<TPartialIFieldSet>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteFieldSet(entityData: IFieldSet) {
  const callingMn = deleteFieldSet.name
  const apiPath = apiEntityPath
  return httpDelete<IFieldSet>({
    callingMn,
    apiPath,
    entityData,
  })
}

//
// Helper Functions
//

export function convertFieldSetFieldsToEntityMaskItems(
  fieldSetItemsArray: TFieldSetItem[],
  companyId?: TEntityId
): TEntityMaskItem[] {
  const fn = `${mn} convertFieldSetFieldsToEntityMaskItems |`

  if (!fieldSetItemsArray) {
    return []
  }
  console.debug(`${fn} fieldSetItemsArray:`, fieldSetItemsArray)
  const theEntityMaskItems: TEntityMaskItem[] = [] // What we're building
  fieldSetItemsArray
    .sort((a, b) => a.seqNum - b.seqNum)
    .forEach((field: TFieldSetItem, index, array) => {
      interface Defaults {
        itemsString?: string
        validations?: TFieldSetFieldValidations
        isKeyIdentifierField: boolean | undefined
      }

      const fillers: Defaults = {
        itemsString: undefined,
        validations: undefined,
        isKeyIdentifierField: undefined,
      }

      const {
        id, // We want to exclude fieldSetField Id
        hrid, // and hrid
        name,
        label,
        selectFrom,
        itemsString,
        doAllowMultipleSelections,
        validations,
        action,
        actionField,
        actionVerb,
        actionFieldValue,
        isKeyIdentifierField,
        ...rest
      } = Object.assign(fillers, field)

      const entityMaskItem = rest as unknown as TEntityMaskItem

      if (action) {
        console.debug(
          `${fn} ${name} has action '${action}'` + `, actionField:`,
          actionField,
          +`, actionFieldValue '${actionFieldValue}'`
        )
        if (action === 'Render if') {
          entityMaskItem.getStateForUnrendered =
            _getStateForUnrenderedOrDisabledFunctionFactory({
              actionField,
              actionVerb,
              actionFieldValue,
            })
          if (isEntityMaskData(entityMaskItem)) {
            entityMaskItem.clearOnUnrender = true
          }
        } else if (action === 'Enable if' && isEntityMaskData(entityMaskItem)) {
          entityMaskItem.getStateForDisabled =
            _getStateForUnrenderedOrDisabledFunctionFactory({
              actionField,
              actionVerb,
              actionFieldValue,
            })
          if (isEntityMaskData(entityMaskItem)) {
            entityMaskItem.clearOnDisable = true
          }
        }
        if (actionField) {
          // Note: 'id' is a fieldname in actionField object
          const actionFieldParentEntityMaskItem = theEntityMaskItems.find(
            (emi) => emi.name === actionField.id
          )
          if (
            isEntityMaskData(entityMaskItem) &&
            actionFieldParentEntityMaskItem && // type guard
            isEntityMaskData(actionFieldParentEntityMaskItem) // ditto
          ) {
            console.debug(
              `${fn} name = ${name}, type = ${entityMaskItem.type} actionFieldParentEntityMaskItem =`,
              actionFieldParentEntityMaskItem
            )
            actionFieldParentEntityMaskItem.clearable = true

            if (!actionFieldParentEntityMaskItem.clearOtherOnChanged) {
              actionFieldParentEntityMaskItem.clearOtherOnChanged = [name]
              console.log(`Setting clearOtherOnChanged to be '${name}'`)
            } else {
              !actionFieldParentEntityMaskItem.clearOtherOnChanged.includes(
                name
              )
                ? actionFieldParentEntityMaskItem.clearOtherOnChanged.push(name)
                : console.log(
                    `${fn} Item '${name}' is already in clearOtherOnChanged array`
                  )
            }
          }
        }
      }

      if (isEntityMaskData(entityMaskItem)) {
        if (itemsString) {
          const parsedItemsStringArray = itemsString.split('\n')
          console.debug(
            `${fn} parsedItemsStringArray =`,
            parsedItemsStringArray
          )
          const items = parsedItemsStringArray.map((value: string) => {
            return value
          })
          console.debug(`${fn} items =`, items)
          entityMaskItem.items = items
          if (doAllowMultipleSelections) {
            entityMaskItem.multiple = true
            entityMaskItem.chips = true
          }
        }

        if (selectFrom?.id === selectFromOptionIds.SUPPLIERS) {
          entityMaskItem.items = []
          entityMaskItem.setSelectItems = async function (
            inputFields: IEntityMaskData[],
            entityParentObjectForCreate: IEntityParentObjectForCreate
          ) {
            await setItemsForSelect({
              calledFromFn:
                '[...convertedFieldSetFieldEntityMaskItem] (supplier) setSelectItems | ',
              inputFields,
              targetFieldName: name,
              getService: getSuppliers,
              labelPropertyName: 'descr',
            })
          }
        }

        if (selectFrom?.id === selectFromOptionIds.LOCATIONS && companyId) {
          entityMaskItem.items = []
          entityMaskItem.setSelectItems = async function (
            inputFields: IEntityMaskData[],
            entityParentObjectForCreate: IEntityParentObjectForCreate
          ) {
            await setItemsForSelect({
              calledFromFn:
                '[...convertedFieldSetFieldEntityMaskItem] (locations) setSelectItems | ',
              inputFields,
              targetFieldName: name,
              getService: getLocationsForCompanyId,
              getServiceId: companyId,
              labelPropertyName: 'nickNamePlusAddressLine1Descr',
            })
          }
        }

        if (validations) {
          const validationObject: IEntityFieldValidation = Object.fromEntries(
            validations.map((v) => [v, true])
          )
          console.debug(`${fn} validationObject =`, validationObject)
          entityMaskItem.validation = validationObject
        }
        if (isKeyIdentifierField) {
          if (isKeyIdentifierField) {
            entityMaskItem.highlight = true
          }
        }
      }

      if (!(isDivider(entityMaskItem) || isSpacer(entityMaskItem))) {
        entityMaskItem.name = name
        entityMaskItem.label = label
        if (isEntityMaskData(entityMaskItem)) {
          if (entityMaskItem.doReturnObject) {
            entityMaskItem.value = null
            entityMaskItem.start = null
          } else {
            entityMaskItem.value = ''
            entityMaskItem.start = ''
          }
        }
      }

      theEntityMaskItems.push(entityMaskItem)
    })
  console.debug(`${fn} theEntityMaskItems:`, theEntityMaskItems)
  return theEntityMaskItems
}

//
// Helpers functions for the helper functions
//

function _getStateForUnrenderedOrDisabledFunctionFactory({
  actionField,
  actionVerb,
  actionFieldValue,
}: {
  actionField: ISelectItem
  actionVerb: TFieldSetFieldActionVerb
  actionFieldValue: string
}) {
  return function (
    inputFields: IEntityMaskData[],
    entityData: IEntityDataItem
  ) {
    return _doUnrenderOrDisable({
      inputFields,
      actionField,
      actionVerb,
      actionFieldValue,
    })
  }
}

function _doUnrenderOrDisable({
  inputFields,
  actionField,
  actionVerb,
  actionFieldValue,
}: {
  inputFields: IEntityMaskData[]
  actionField: ISelectItem
  actionVerb: TFieldSetFieldActionVerb
  actionFieldValue: string
}) {
  const fn = '[services/fieldSets.ts] _doUnrenderOrDisable |'
  {
    const f = inputFields.find(
      (ifield) => ifield.name === actionField.id
    ) as IEntityMaskData

    console.debug(
      `${fn} |${actionField.id}| |${actionFieldValue}| |${f?.value}|`
    )

    if (!(typeof f?.value === 'string') && !Array.isArray(f?.value)) {
      return true
    }

    console.debug(
      `${fn} ------>>> trigger: ${actionField.id} ${actionVerb} ${actionFieldValue}, f.value = ${f.value}`
    )

    if (Array.isArray(f.value)) {
      // 'f' is a select field with 'allow multiple' true
      console.debug(`${fn} f.value is an array`)
      if (
        (actionVerb === 'Is' &&
          (f.value.length > 1 || !f.value.includes(actionFieldValue))) ||
        (actionVerb === 'Is not' &&
          (f.value.includes(actionFieldValue) ||
            (actionFieldValue === 'Empty' && f.value.length === 0))) ||
        (actionVerb === 'Includes' &&
          (f.value.length === 0 || !f.value.includes(actionFieldValue))) ||
        (actionVerb === 'Excludes' &&
          (f.value.length === 0 || f.value.includes(actionFieldValue)))
      ) {
        console.debug(`${fn} returning true`)
        return true
      }
      console.debug(`${fn} returning false`)
      return false
    } else {
      // 'f' is either just a text field,
      // or it's a select with 'allow multiple' false
      // or it's a select with 'allow multiple' true but no f.value
      console.debug(`${fn} f.value is -NOT- an array`)
      if (
        (actionVerb === 'Is' && f.value !== actionFieldValue) ||
        (actionVerb === 'Is not' &&
          ((actionFieldValue === 'Empty' && !f.value) ||
            (actionFieldValue !== 'Empty' && f.value === actionFieldValue))) ||
        ['Includes', 'Excludes'].includes(actionVerb)
      ) {
        console.debug(`${fn} returning true`)
        return true
      }
      console.debug(`${fn} returning false`)
      return false
    }

    return ['divider', 'subtitle', 'spacer', ''].includes(f.value)
  }
}
